import { FormCard } from '@qbe/ads-blocks-forms';
import { Box, Button, HighlightContainer, Spacer, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';

import { MembershipConfirmationPanel } from '~/components/MembershipConfirmationPanel/MembershipConfirmationPanel';
import { useSnackbar } from '~/components-ds/Snackbar';
import { useAppContent, useAppSelector, useBusinessDetails, useExtraInfo, useQuote } from '~/hooks';
import { useGetConfirmationReviewVariant } from '~/hooks/useGetConfirmationReviewVariant';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { useBreakpoint } from '~/hooks-ds/useBreakpoint';
import { selectIsReferralVelocity, selectIsStaff } from '~/selectors/selectAppDetails';
import { DateFormat, utcDateFormatter } from '~/utils';

import { ProductRatingPanel } from '../../components/ProductRatingPanel';
import SurveyNPS from '../../components/SurveyNPS';

const Confirmation: React.FC = () => {
  const reviewVariant = useGetConfirmationReviewVariant();
  const isStaff = useAppSelector(selectIsStaff);
  const hasMembership = useAppSelector(selectIsReferralVelocity);
  const { coverStart } = useBusinessDetails();
  const { firstName, email } = useExtraInfo();
  const { isMobile } = useBreakpoint();
  const { policyNumber } = useQuote();
  const { enqueueSnackbar } = useSnackbar();
  const { groups } = useAppSelector((state) => state.appDetails);
  const filteredGroups = groups ? groups.filter((group) => group !== 'staff') : [];
  const intermediaryCode = filteredGroups.length > 0 ? filteredGroups[0] : '';

  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.confirmation',
    tokens: {
      firstName: () => firstName || '',
      email: () => email || '',
    },
  });

  const handleNext = () => {
    window.location.href = getSharedContent('qbeHome');
  };

  useEffect(() => {
    if (!isStaff && reviewVariant === 'nps') {
      enqueueSnackbar('', {
        variant: 'survey',
        children: SurveyNPS,
        persist: true,
        preventDuplicate: true,
      });
    }
  }, [enqueueSnackbar, isStaff, reviewVariant]);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <Box data-testid="review-pay-payments">
      <FormCard
        title={getTextContent('title')}
        variant="pageTitle"
        description={getTextContent('description')}
      />
      <Spacer size="md" />
      <FormCard variant="content">
        <HighlightContainer>
          <Typography variant="subtitle2" component="h2" fontWeight="fontWeightBold">
            {isMobile
              ? getTextContent('fields.policyNumber.mobile')
              : getTextContent('fields.policyNumber.title')}{' '}
            {policyNumber}
          </Typography>
        </HighlightContainer>
        <Spacer size="md" />
        <Typography component="h4" variant="label1" fontWeight="fontWeightMedium" gutterBottom>
          {getTextContent('fields.coverStart.title')}
        </Typography>
        <Typography component="p" variant="label1" fontWeight="fontWeightBold" gutterBottom>
          {utcDateFormatter(coverStart!, DateFormat.ddMMyyyy)}
        </Typography>
        <Spacer size="md" />
        <Typography textAlign="center" gutterBottom>
          {getJSXContent('content.helper.description')}
        </Typography>
        <Spacer size="md" />
        <Button
          variant="secondary-on-light"
          data-track={getSharedContent('homeButtonLabel')}
          onClick={handleNext}
          label={getSharedContent('homeButtonLabel')}
        />
      </FormCard>
      <Spacer size="md" />
      {hasMembership && intermediaryCode && (
        <>
          <MembershipConfirmationPanel intermediaryCode={intermediaryCode} />
          <Spacer size="md" />
        </>
      )}
      <ProductRatingPanel />
    </Box>
  );
};

export default Confirmation;
